import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(
  price: number | undefined | null,
  currency: string = "₦"
): string {
  if (price === undefined || price === null || isNaN(price)) {
    return `${currency}0`;
  }
  return `${currency}${price.toLocaleString()}`;
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) {
    return `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}hr${hours > 1 ? "s" : ""}`;
  }
  return `${hours}hr ${mins}min`;
}

export function toSentenceCase(categoryName: string): string {
  return categoryName.replace(/_/g, " ");
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/--+/g, "-")
    .trim();
}

export function formatTime(timeString?: string): string {
  if (!timeString) return "";

  const [hours, minutes] = timeString.split(":");

  // Check if hours or minutes are invalid
  if (
    !hours ||
    !minutes ||
    isNaN(parseInt(hours, 10)) ||
    isNaN(parseInt(minutes, 10))
  ) {
    return "";
  }

  let period = "AM";
  let formattedHours = parseInt(hours, 10);

  if (formattedHours > 12) {
    formattedHours -= 12;
    period = "PM";
  } else if (formattedHours === 12) {
    period = "PM";
  } else if (formattedHours === 0) {
    formattedHours = 12;
  }

  // Ensure minutes are always displayed with 2 digits
  const formattedMinutes = minutes.padStart(2, "0");

  return `${formattedHours}:${formattedMinutes} ${period}`;
}

export function groupWorkingHoursByDay(hours: any[]) {
  if (!hours || !Array.isArray(hours)) return [];

  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  // Sort data by day and openTime (operational order, not chronological)
  const sortedData = [...hours].sort((a, b) => {
    const dayDiff =
      daysOrder.indexOf(a.day.toLowerCase()) -
      daysOrder.indexOf(b.day.toLowerCase());
    if (dayDiff !== 0) return dayDiff;

    // Compare openTime within the same day, nulls last
    if (!a.openTime && b.openTime) return 1;
    if (a.openTime && !b.openTime) return -1;
    if (!a.openTime && !b.openTime) return 0;

    // For operational order: if a business operates across midnight (e.g., 10am-5am),
    // we want to show the earlier opening time first (10am before 12am)
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    const aMinutes = aTime[0] * 60 + aTime[1];
    const bMinutes = bTime[0] * 60 + bTime[1];

    // Check if this day has hours that span midnight
    const dayHours = hours.filter(
      (h) =>
        h.day.toLowerCase() === a.day.toLowerCase() && h.openTime && h.closeTime
    );
    const hasMidnightSpan = dayHours.some((h) => {
      const [openH, openM] = h.openTime.split(":").map(Number);
      const [closeH, closeM] = h.closeTime.split(":").map(Number);
      const openMinutes = openH * 60 + openM;
      const closeMinutes = closeH * 60 + closeM;
      return closeMinutes < openMinutes; // Close time is earlier than open time = spans midnight
    });

    if (hasMidnightSpan) {
      // For midnight-spanning hours, sort by operational order (earliest opening first)
      // This ensures 10am comes before 12am for a 10am-5am business
      return aMinutes - bMinutes;
    } else {
      // For normal hours, use chronological order
      return a.openTime.localeCompare(b.openTime);
    }
  });

  // Group by day
  const groupedData = daysOrder.map((day) => {
    const times = sortedData
      .filter(
        (hour) =>
          hour.day.toLowerCase() === day && hour?.openTime && hour?.closeTime
      )
      .map(({ openTime, closeTime }) => ({ openTime, closeTime }));

    return {
      day,
      times,
    };
  });

  return groupedData;
}

// Interface for closed period data
export interface ClosedPeriod {
  id?: string;
  title?: string;
  start_at?: string;  // REST API format (snake_case)
  end_at?: string;    // REST API format (snake_case)
  startAt?: string;   // GraphQL format (camelCase)
  endAt?: string;     // GraphQL format (camelCase)
}

// Helper function to check if current time falls within any closed period
export function isWithinClosedPeriod(
  closedPeriods: ClosedPeriod[],
  businessTimezone?: string
): boolean {
  if (!closedPeriods || !Array.isArray(closedPeriods) || closedPeriods.length === 0) {
    return false;
  }

  const now = new Date();

  return closedPeriods.some((period) => {
    // Handle both snake_case (REST) and camelCase (GraphQL) formats
    const startAtStr = period.start_at || period.startAt;
    const endAtStr = period.end_at || period.endAt;

    if (!startAtStr || !endAtStr) return false;

    const startAt = new Date(startAtStr);
    const endAt = new Date(endAtStr);

    // Check if current time falls within the closed period
    return now >= startAt && now <= endAt;
  });
}

// Helper function to find the end of the current closed period
export function getCurrentClosedPeriodEnd(
  closedPeriods: ClosedPeriod[],
  businessTimezone?: string
): Date | null {
  if (!closedPeriods || !Array.isArray(closedPeriods) || closedPeriods.length === 0) {
    return null;
  }

  const now = new Date();

  for (const period of closedPeriods) {
    const startAtStr = period.start_at || period.startAt;
    const endAtStr = period.end_at || period.endAt;

    if (!startAtStr || !endAtStr) continue;

    const startAt = new Date(startAtStr);
    const endAt = new Date(endAtStr);

    if (now >= startAt && now <= endAt) {
      return endAt;
    }
  }

  return null;
}

export function getCurrentOpenStatus(
  hours: any[],
  businessTimezone?: string,
  closedPeriods?: ClosedPeriod[]
) {
  if (!hours || !Array.isArray(hours)) {
    return { isOpen: false, todayHours: null, nextOpenTime: null, closedPeriodEnd: null };
  }

  // First check if we're within a closed period
  const inClosedPeriod = isWithinClosedPeriod(closedPeriods || [], businessTimezone);
  if (inClosedPeriod) {
    const closedPeriodEnd = getCurrentClosedPeriodEnd(closedPeriods || [], businessTimezone);
    // Find next open time after the closed period ends
    const nextOpenTime = findNextOpenTimeAfterDate(
      hours,
      closedPeriodEnd || new Date(),
      businessTimezone
    );
    return { isOpen: false, todayHours: null, nextOpenTime, closedPeriodEnd };
  }

  // Get current time in the business's timezone
  const now = new Date();
  let businessTime: Date;

  if (businessTimezone) {
    // Convert current UTC time to business timezone
    businessTime = new Date(
      now.toLocaleString("en-US", { timeZone: businessTimezone })
    );
  } else {
    // Fallback to local time (current behavior)
    businessTime = now;
  }

  const currentDay = businessTime
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const currentTime = businessTime.getHours() * 60 + businessTime.getMinutes(); // Current time in minutes since midnight

  // Get today's hours
  const todayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === currentDay && hour?.openTime && hour?.closeTime
  );

  if (todayHours.length === 0) {
    // If no hours today, find next opening time
    const nextOpenTime = findNextOpenTime(
      hours,
      businessTime,
      businessTimezone
    );
    return { isOpen: false, todayHours: null, nextOpenTime, closedPeriodEnd: null };
  }

  // Check if currently open in any of today's time slots
  const isOpen = todayHours.some((slot) => {
    const [openHours, openMinutes] = slot.openTime.split(":").map(Number);
    const [closeHours, closeMinutes] = slot.closeTime.split(":").map(Number);

    const openTime = openHours * 60 + openMinutes;
    const closeTime = closeHours * 60 + closeMinutes;

    // Handle businesses that span midnight (e.g., 22:00 - 05:00)
    if (closeTime < openTime) {
      // Business spans midnight - check if current time is after open OR before close
      return currentTime >= openTime || currentTime <= closeTime;
    } else {
      // Normal business hours - check if current time is between open and close
      return currentTime >= openTime && currentTime <= closeTime;
    }
  });

  // If closed, find next opening time
  const nextOpenTime = isOpen
    ? null
    : findNextOpenTime(hours, businessTime, businessTimezone);

  return { isOpen, todayHours, nextOpenTime, closedPeriodEnd: null };
}

// Helper function to find next open time after a specific date (e.g., after closed period ends)
function findNextOpenTimeAfterDate(
  hours: any[],
  afterDate: Date,
  businessTimezone?: string
) {
  if (!hours || !Array.isArray(hours)) return null;

  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  // Convert afterDate to business timezone
  let checkDate: Date;
  if (businessTimezone) {
    checkDate = new Date(
      afterDate.toLocaleString("en-US", { timeZone: businessTimezone })
    );
  } else {
    checkDate = new Date(afterDate);
  }

  const afterTime = checkDate.getHours() * 60 + checkDate.getMinutes();
  const afterDay = checkDate
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();

  // Check same day first (hours after the afterDate time)
  const sameDayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === afterDay && hour?.openTime && hour?.closeTime
  );

  // Sort and find next slot on same day
  const sortedSameDayHours = [...sameDayHours].sort((a, b) => {
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
  });

  for (const slot of sortedSameDayHours) {
    const [openHours, openMinutes] = slot.openTime.split(":").map(Number);
    const openTime = openHours * 60 + openMinutes;

    if (openTime > afterTime) {
      const userTime = businessTimezone
        ? convertBusinessTimeToUserTimezone(slot.openTime, businessTimezone)
        : slot.openTime;
      return { day: afterDay, time: userTime, date: new Date(checkDate) };
    }
  }

  // Check next 90 days (extended to handle longer closed periods)
  for (let i = 1; i <= 90; i++) {
    const nextDate = new Date(checkDate);
    nextDate.setDate(checkDate.getDate() + i);
    const nextDay = nextDate
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    const dayHours = hours.filter(
      (hour) =>
        hour.day.toLowerCase() === nextDay && hour?.openTime && hour?.closeTime
    );

    if (dayHours.length > 0) {
      const sortedHours = [...dayHours].sort((a, b) => {
        const aTime = a.openTime.split(":").map(Number);
        const bTime = b.openTime.split(":").map(Number);
        return aTime[0] * 60 + aTime[1] - (bTime[0] * 60 + bTime[1]);
      });

      const userTime = businessTimezone
        ? convertBusinessTimeToUserTimezone(sortedHours[0].openTime, businessTimezone)
        : sortedHours[0].openTime;
      return { day: nextDay, time: userTime, date: nextDate };
    }
  }

  return null;
}

function findNextOpenTime(
  hours: any[],
  currentDate: Date,
  businessTimezone?: string
) {
  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const currentDayIndex = daysOrder.indexOf(
    currentDate.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase()
  );

  // Check remaining hours today first
  const currentDay = currentDate
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase();
  const currentTime = currentDate.getHours() * 60 + currentDate.getMinutes();

  const todayHours = hours.filter(
    (hour) =>
      hour.day.toLowerCase() === currentDay && hour?.openTime && hour?.closeTime
  );

  // Sort today's hours by opening time to check them in order
  const sortedTodayHours = [...todayHours].sort((a, b) => {
    const aTime = a.openTime.split(":").map(Number);
    const bTime = b.openTime.split(":").map(Number);
    const aMinutes = aTime[0] * 60 + aTime[1];
    const bMinutes = bTime[0] * 60 + bTime[1];
    return aMinutes - bMinutes;
  });

  // Find next opening time today
  for (const slot of sortedTodayHours) {
    const [openHours, openMinutes] = slot.openTime.split(":").map(Number);
    const openTime = openHours * 60 + openMinutes;

    if (openTime > currentTime) {
      // Convert business time to user's local timezone for display
      const userTime = businessTimezone
        ? convertBusinessTimeToUserTimezone(slot.openTime, businessTimezone)
        : slot.openTime;
      return { day: currentDay, time: userTime, date: new Date(currentDate) };
    }
  }

  // If no more hours today, check next 7 days
  for (let i = 1; i <= 7; i++) {
    const checkDate = new Date(currentDate);
    checkDate.setDate(currentDate.getDate() + i);
    const checkDay = checkDate
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    const dayHours = hours.filter(
      (hour) =>
        hour.day.toLowerCase() === checkDay && hour?.openTime && hour?.closeTime
    );

    if (dayHours.length > 0) {
      // Sort by operational order (for midnight-spanning hours) or chronological order
      const sortedHours = dayHours.sort((a, b) => {
        // Check if this day has hours that span midnight
        const hasMidnightSpan = dayHours.some((h) => {
          const [openH, openM] = h.openTime.split(":").map(Number);
          const [closeH, closeM] = h.closeTime.split(":").map(Number);
          const openMinutes = openH * 60 + openM;
          const closeMinutes = closeH * 60 + closeM;
          return closeMinutes < openMinutes; // Close time is earlier than open time = spans midnight
        });

        if (hasMidnightSpan) {
          // For midnight-spanning hours, sort by operational order (earliest opening first)
          const aTime = a.openTime.split(":").map(Number);
          const bTime = b.openTime.split(":").map(Number);
          const aMinutes = aTime[0] * 60 + aTime[1];
          const bMinutes = bTime[0] * 60 + bTime[1];
          return aMinutes - bMinutes;
        } else {
          // For normal hours, use chronological order
          return a.openTime.localeCompare(b.openTime);
        }
      });
      // Convert business time to user's local timezone for display
      const userTime = businessTimezone
        ? convertBusinessTimeToUserTimezone(
            sortedHours[0].openTime,
            businessTimezone
          )
        : sortedHours[0].openTime;
      return { day: checkDay, time: userTime, date: checkDate };
    }
  }

  return null;
}

// Helper function to convert business time to user's local timezone
export function convertBusinessTimeToUserTimezone(
  businessTime: string,
  businessTimezone: string,
  userTimezone?: string
): string {
  if (!businessTimezone) return businessTime;

  try {
    // For now, let's just return the business time as-is to avoid timezone conversion issues
    // This ensures we don't get NaN:undefined errors
    return businessTime;
  } catch (error) {
    console.error("Error converting business time to user timezone:", error);
    return businessTime; // Fallback to original time
  }
}

// Helper function to get user's timezone
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Promotion utility functions
export function isPromotionActive(promotion?: {
  startAt?: string;
  endAt?: string;
  isActive?: boolean;
}): boolean {
  if (!promotion) return false;

  // If isActive is explicitly set, use it
  if (promotion.isActive !== undefined) {
    if (!promotion.isActive) return false;
  }

  const now = new Date();

  // Check start date
  if (promotion.startAt) {
    const startDate = new Date(promotion.startAt);
    if (now < startDate) return false;
  }

  // Check end date
  if (promotion.endAt) {
    const endDate = new Date(promotion.endAt);
    if (now > endDate) return false;
  }

  return true;
}

export function calculateDiscountedPrice(
  originalPrice: number,
  promotion?: {
    discountType: "percentage" | "fixed";
    discountValue: number;
    startAt?: string;
    endAt?: string;
    isActive?: boolean;
  }
): number {
  if (!promotion || !isPromotionActive(promotion)) {
    return originalPrice;
  }

  if (promotion.discountType === "percentage") {
    const discountAmount = (originalPrice * promotion.discountValue) / 100;
    return Math.max(0, originalPrice - discountAmount);
  } else {
    // Fixed discount
    return Math.max(0, originalPrice - promotion.discountValue);
  }
}

export function getDiscountAmount(
  originalPrice: number,
  promotion?: {
    discountType: "percentage" | "fixed";
    discountValue: number;
    startAt?: string;
    endAt?: string;
    isActive?: boolean;
  }
): number {
  if (!promotion || !isPromotionActive(promotion)) {
    return 0;
  }

  if (promotion.discountType === "percentage") {
    return (originalPrice * promotion.discountValue) / 100;
  } else {
    return promotion.discountValue;
  }
}

export function formatPromotionEndDate(endAt?: string): string {
  if (!endAt) return "";

  try {
    const endDate = new Date(endAt);
    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return "";
    if (diffDays === 0) return "Ends today";
    if (diffDays === 1) return "Ends tomorrow";
    if (diffDays <= 7) return `Ends in ${diffDays} days`;

    return `Ends ${endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: endDate.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    })}`;
  } catch {
    return "";
  }
}
